import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Public feedback form, reviewers submit their scorecard + summary here.
// POSTs to /api/reviewer-feedback. Owner views submissions at
// /api/admin/reviewer-feedback?key=... (rendered by the backend).

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Same rubric that lives on the /reviewer-guide page, kept in sync manually.
const SECTIONS = [
  {
    id: "A",
    title: "A. Homepage / landing (/beta)",
    lines: [
      "Hero, is the pitch clear in one sentence?",
      "Who this is for is obvious",
      "The 'how the race works' section",
      "Pricing / Sprint offer clarity",
      "Testimonial / trust signals",
      "Compare page",
      "Features page",
      "Overall visual polish",
    ],
  },
  {
    id: "B",
    title: "B. Sign-up + Google Sheet sync",
    lines: [
      "Free Sprint CTA feels safe (not a trick)",
      "Checkout is calm and trustworthy",
      "Magic-link email tone",
      "Google Drive authorization step (clear? scary?)",
      "The moment the sheet appears in your Drive, is it obvious it worked?",
      "First landing on /app, is Step 1 obvious?",
    ],
  },
  {
    id: "C",
    title: "C. Mobile experience (Track 1)",
    lines: [
      "Log Income button. One-thumb reachable?",
      "Text/labels, legible with no overlap?",
      "Dashboard hierarchy on mobile (Today / Week / More)",
      "Time Off / PTO tagging on mobile",
      "Mobile bottom nav, clear what each icon does?",
      "Caddy AI chat, fast enough on mobile?",
      "Add-to-Home-Screen flow (if you tried it)",
    ],
  },
  {
    id: "D",
    title: "D. Desktop experience (Track 2)",
    lines: [
      "Sidebar / desktop layout",
      "Yearly Scoreboard, information architecture",
      "Resource Library, feels authoritative?",
      "Journal + Monthly Wrap-Up on desktop",
      "Google Sheet organization (Legend, Scoreboard, weekly tabs)",
      "Season Setup gate on desktop",
    ],
  },
  {
    id: "E",
    title: "E. The handoff (Track 3, cross-device)",
    lines: [
      "Mobile-logged wins appear correctly on desktop Scoreboard",
      "Mobile-logged wins appear correctly in the Google Sheet",
      "Google Sheet numbers match app numbers (no drift, no delay)",
      "Does moving between app and sheet feel like ONE system?",
      "Where (if anywhere) did the connection feel broken?",
    ],
  },
  {
    id: "F",
    title: "F. The Sprint experience (Days 1–28)",
    lines: [
      "Locked sections during the Sprint, motivating vs. frustrating",
      "Day-27 'converts tomorrow' message, honest, spammy, or scary?",
      "The overall Sprint tone, does it earn trust?",
    ],
  },
  {
    id: "G",
    title: "G. The conversion moment",
    lines: [
      "The Season Setup gate copy",
      "'Keep my settings' path clarity",
      "'Recalibrate' path, using the calculator",
      "Emotional landing after the gate closes",
    ],
  },
  {
    id: "H",
    title: "H. Emotional / brand",
    lines: [
      "Does the 'relay race' metaphor stay useful or get tiring?",
      "Does it feel built for freelancers or generic finance?",
      "Does it feel honest about money?",
      "Would you tell a friend to try the Sprint?",
    ],
  },
];

const styles = `
  .rf-page {
    background: var(--pr-bg);
    color: var(--pr-ink);
    min-height: 100vh;
    padding: 48px 20px 96px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .rf-wrap { max-width: 780px; margin: 0 auto; }
  .rf-eyebrow {
    display: inline-block;
    background: var(--pr-tint-gold);
    color: var(--pr-accent-gold-text);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: 999px;
    margin-bottom: 18px;
  }
  .rf-h1 {
    font-family: "Fraunces", Georgia, serif;
    font-size: 34px;
    line-height: 1.15;
    margin: 0 0 10px;
    color: var(--pr-ink);
  }
  .rf-lede {
    font-size: 16px;
    line-height: 1.55;
    color: var(--pr-ink2);
    margin: 0 0 28px;
    border-left: 3px solid var(--pr-gold);
    padding-left: 14px;
  }
  .rf-card {
    background: var(--pr-card);
    border: 1px solid var(--pr-line);
    border-radius: 12px;
    padding: 22px 24px;
    margin-bottom: 20px;
  }
  .rf-card h2 {
    font-family: "Fraunces", Georgia, serif;
    font-size: 20px;
    margin: 0 0 14px;
    color: var(--pr-ink);
  }
  .rf-row {
    display: grid;
    grid-template-columns: 1.6fr 90px 2fr;
    gap: 10px;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px dashed var(--pr-line);
  }
  .rf-row:last-child { border-bottom: none; }
  .rf-row .label { font-size: 14px; color: var(--pr-ink); line-height: 1.4; }
  .rf-rating, .rf-note-input, .rf-input, .rf-textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--pr-input-line);
    border-radius: 8px;
    background: var(--pr-input-bg);
    color: var(--pr-ink);
    font-size: 14px;
    font-family: inherit;
  }
  .rf-textarea { min-height: 140px; resize: vertical; line-height: 1.55; }
  .rf-label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: var(--pr-ink);
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .rf-help {
    font-size: 13px;
    color: var(--pr-muted);
    margin-top: 6px;
    line-height: 1.5;
  }
  .rf-btn {
    display: inline-block;
    padding: 12px 26px;
    background: var(--pr-gold);
    color: #2A1E5C;
    border: 1px solid var(--pr-gold);
    border-radius: 999px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s;
  }
  .rf-btn:hover:not(:disabled) { background: #E8B841; transform: translateY(-1px); }
  .rf-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .rf-error {
    background: var(--pr-tint-red);
    border: 1px solid var(--pr-red);
    color: var(--pr-red);
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    margin: 12px 0;
  }
  .rf-success {
    background: var(--pr-tint-green);
    border: 1px solid var(--pr-green);
    color: var(--pr-accent-green-text);
    padding: 22px 26px;
    border-radius: 12px;
    font-size: 16px;
    line-height: 1.55;
  }
  @media (max-width: 620px) {
    .rf-row { grid-template-columns: 1fr; gap: 6px; padding: 12px 0; }
    .rf-row .label { font-weight: 700; }
  }
`;

export default function ReviewerFeedbackPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [ratings, setRatings] = useState({});
  const [summary, setSummary] = useState("");
  const [teardown, setTeardown] = useState("");
  const [crossDevice, setCrossDevice] = useState("");
  const [devicesUsed, setDevicesUsed] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Submit reviewer feedback, Payday Relay";
  }, []);

  const setRating = (sectionTitle, line, field, value) => {
    setRatings((prev) => ({
      ...prev,
      [sectionTitle]: {
        ...(prev[sectionTitle] || {}),
        [line]: {
          ...((prev[sectionTitle] || {})[line] || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Please enter your name and email so we can follow up.");
      return;
    }
    const cd = crossDevice === "" ? null : parseInt(crossDevice, 10);
    if (cd !== null && (isNaN(cd) || cd < 1 || cd > 10)) {
      setError("Cross-device score must be a number 1–10 (or leave blank).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/reviewer-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewer_name: name.trim(),
          reviewer_email: email.trim(),
          ratings,
          summary_paragraph: summary,
          open_teardown: teardown,
          cross_device_score: cd,
          devices_used: devicesUsed,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Something went wrong. Try again in a moment.");
      } else {
        setDone(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch {
      setError("Network error, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rf-page" data-testid="reviewer-feedback-page">
        <style>{styles}</style>
        <div className="rf-wrap">
          <div className="rf-success" data-testid="reviewer-feedback-success">
            <b>Thank you.</b> Your feedback is in.
            <br /><br />
            The owner will see it in the admin dashboard within a minute. If you have more thoughts
            later, screenshots, a follow-up teardown, feel free to submit again from the same
            email; nothing gets overwritten.
            <br /><br />
            <Link to="/reviewer-guide" style={{ color: "var(--pr-accent-green-text)" }}>
              ← Back to the reviewer guide
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-page" data-testid="reviewer-feedback-page">
      <style>{styles}</style>
      <div className="rf-wrap">
        <span className="rf-eyebrow">Internal · UX Review</span>
        <h1 className="rf-h1">Submit your reviewer feedback</h1>
        <p className="rf-lede">
          Please frame every note as <b>Problem · Impact · Fix</b> where you can, vague feelings
          burn time; actionable directives ship in one prompt. See the{" "}
          <Link to="/reviewer-guide" style={{ color: "var(--pr-ink)" }}>reviewer guide</Link> if you
          need the rubric context.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="rf-card">
            <h2>Who's this from?</h2>
            <div style={{ marginBottom: 14 }}>
              <label className="rf-label" htmlFor="rf-name">Your name</label>
              <input
                id="rf-name"
                className="rf-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Alex Draft"
                data-testid="reviewer-feedback-name"
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label className="rf-label" htmlFor="rf-email">Your email</label>
              <input
                id="rf-email"
                className="rf-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@studio.com"
                data-testid="reviewer-feedback-email"
              />
            </div>
            <div>
              <label className="rf-label" htmlFor="rf-devices">Devices you used</label>
              <input
                id="rf-devices"
                className="rf-input"
                value={devicesUsed}
                onChange={(e) => setDevicesUsed(e.target.value)}
                placeholder="e.g. iPhone 15 Safari + MacBook Air Chrome"
                data-testid="reviewer-feedback-devices"
              />
              <div className="rf-help">Helps us weight mobile-vs-desktop findings.</div>
            </div>
          </div>

          {SECTIONS.map((section) => (
            <div className="rf-card" key={section.id}>
              <h2>{section.title}</h2>
              {section.lines.map((line, i) => (
                <div className="rf-row" key={i}>
                  <div className="label">{line}</div>
                  <input
                    className="rf-rating"
                    type="number"
                    min="1"
                    max="5"
                    placeholder="1–5"
                    value={ratings[section.title]?.[line]?.rating || ""}
                    onChange={(e) => setRating(section.title, line, "rating", e.target.value)}
                    aria-label={`Rating for ${line}`}
                    data-testid={`reviewer-feedback-rating-${section.id}-${i}`}
                  />
                  <input
                    className="rf-note-input"
                    type="text"
                    placeholder="Problem · Impact · Fix (optional)"
                    value={ratings[section.title]?.[line]?.note || ""}
                    onChange={(e) => setRating(section.title, line, "note", e.target.value)}
                    aria-label={`Note for ${line}`}
                    data-testid={`reviewer-feedback-note-${section.id}-${i}`}
                  />
                </div>
              ))}
            </div>
          ))}

          <div className="rf-card">
            <h2>The big cross-device question</h2>
            <label className="rf-label" htmlFor="rf-crossdevice">
              On a scale of 1–10, how seamlessly could you move between phone, laptop, and your Google Sheet?
            </label>
            <input
              id="rf-crossdevice"
              className="rf-input"
              type="number"
              min="1"
              max="10"
              placeholder="1–10"
              value={crossDevice}
              onChange={(e) => setCrossDevice(e.target.value)}
              data-testid="reviewer-feedback-cross-device"
            />
            <div className="rf-help">Where did the connection feel broken? Note it in the teardown below.</div>
          </div>

          <div className="rf-card">
            <h2>One-paragraph summary</h2>
            <p className="rf-help" style={{ marginTop: 0, marginBottom: 10 }}>
              4–6 sentences: strongest thing, weakest thing, and if you had to fix ONE screen tomorrow to
              move the needle, which one and why.
            </p>
            <textarea
              className="rf-textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="The strongest thing is… The weakest thing is… If I had to fix one screen…"
              data-testid="reviewer-feedback-summary"
            />
          </div>

          <div className="rf-card">
            <h2>Open teardown</h2>
            <p className="rf-help" style={{ marginTop: 0, marginBottom: 10 }}>
              Everything else, copy nits, ideas, weird moments. <b>Problem · Impact · Fix</b> for
              every item where possible.
            </p>
            <textarea
              className="rf-textarea"
              style={{ minHeight: 240 }}
              value={teardown}
              onChange={(e) => setTeardown(e.target.value)}
              placeholder={"• Problem:\n  Impact:\n  Fix: Move the Log button to the top-left and increase its size by 20%."}
              data-testid="reviewer-feedback-teardown"
            />
          </div>

          {error && <div className="rf-error" data-testid="reviewer-feedback-error">{error}</div>}

          <button
            className="rf-btn"
            type="submit"
            disabled={submitting}
            data-testid="reviewer-feedback-submit"
          >
            {submitting ? "Submitting…" : "Submit feedback →"}
          </button>
        </form>
      </div>
    </div>
  );
}
